import React from "react";
import { IGaiaobjectNameProps } from "./definitions";
import { Input } from "@amzn/awsui-components-react";
import FormField from "@amzn/awsui-components-react/polaris/form-field";
import styles from "./style.module.scss";
import { cap, getYearRange } from "utils/date-format";
import { getEntityNameFromEntityCompositeKey } from "features/ui-content/utils";

export enum GaiaObjectCategory {
  Notice = "notice",
  Litigation = "litigation",
  Audit = "audit",
}

export const formatTaxAuthority = (
  country?: string,
  governmentEntity?: string,
  localJurisdiction?: string
): string => {
  let taxAuthority = "";
  if (country) {
    if (governmentEntity && localJurisdiction) {
      taxAuthority = `${country}-${governmentEntity}-${localJurisdiction}`;
    } else if (governmentEntity) {
      taxAuthority = `${country}-${governmentEntity}`;
    } else if (localJurisdiction) {
      taxAuthority = `${country}-${localJurisdiction}`;
    } else {
      taxAuthority = country;
    }
  }
  return taxAuthority;
};

export const formatTypeField = (typeField?: string | string[]): string => {
  let formattedType = "";
  if (Array.isArray(typeField)) {
    if (typeField.length === 0) {
      formattedType = "";
    } else if (typeField.length <= 2) {
      formattedType = typeField.join(", ");
    } else {
      formattedType = `${typeField[0]}, ${typeField[1]}, +${
        typeField.length - 2
      }`;
    }
  } else {
    formattedType = typeField ?? "";
  }
  return formattedType;
};

export const transformEntityForDisplay = (
  entityValue?: string,
  entityCode?: string
): string => {
  if (!entityValue || !entityCode) return entityValue || "";

  // Strip composite key format if present
  const cleanedName = getEntityNameFromEntityCompositeKey(entityValue);

  const truncatedName =
    cleanedName.length > 40 ? cleanedName.substring(0, 40) : cleanedName;
  return `${entityCode} - ${truncatedName}`;
};

export function getGaiaobjectName(props: IGaiaobjectNameProps): string {
  switch (props.category) {
    case GaiaObjectCategory.Notice:
      return generateNoticeName(props);
    case GaiaObjectCategory.Litigation:
      return generateLitigationName(props);
    default:
      return "";
  }
}

export const generateNoticeName = (props: IGaiaobjectNameProps): string => {
  const {
    country,
    governmentEntity,
    localJurisdiction,
    entity,
    noticeType,
    taxPeriodStartDate,
    taxPeriodEndDate,
    titleSuffix,
    entityCode,
    taxTypes,
  } = props;

  if (
    !country &&
    !governmentEntity &&
    !localJurisdiction &&
    !entity &&
    !noticeType &&
    !taxTypes &&
    (!taxPeriodStartDate || taxPeriodStartDate === "") &&
    (!taxPeriodEndDate || taxPeriodEndDate === "") &&
    (!titleSuffix || titleSuffix === "")
  ) {
    return "";
  }

  const taxAuthority = formatTaxAuthority(
    country,
    governmentEntity,
    localJurisdiction
  );

  const formattedNoticeType = formatTypeField(noticeType);
  const formattedTaxType = formatTypeField(taxTypes);
  const transformedEntity = transformEntityForDisplay(entity, entityCode);

  return `${taxAuthority.trim()} | ${transformedEntity.trim()} | ${formattedNoticeType} | ${
    getYearRange(taxPeriodStartDate, taxPeriodEndDate) ?? ""
  } | ${formattedTaxType}${titleSuffix ? ` | ${titleSuffix}` : ""}`.trim();
};

export const generateLitigationName = (props: IGaiaobjectNameProps): string => {
  const {
    country,
    governmentEntity,
    localJurisdiction,
    entity,
    litigationType,
    taxPeriodStartDate,
    taxPeriodEndDate,
    titleSuffix,
    entityCode,
    taxTypes,
  } = props;

  if (
    !country &&
    !governmentEntity &&
    !localJurisdiction &&
    !entity &&
    !litigationType &&
    !taxTypes &&
    (!taxPeriodStartDate || taxPeriodStartDate === "") &&
    (!taxPeriodEndDate || taxPeriodEndDate === "") &&
    (!titleSuffix || titleSuffix === "")
  ) {
    return "";
  }

  const taxAuthority = formatTaxAuthority(
    country,
    governmentEntity,
    localJurisdiction
  );

  const formattedLitigationType = formatTypeField(litigationType);
  const formattedTaxType = formatTypeField(taxTypes);
  const transformedEntity = transformEntityForDisplay(entity, entityCode);

  return `${taxAuthority.trim()} | ${transformedEntity.trim()} | ${formattedLitigationType} | ${
    getYearRange(taxPeriodStartDate, taxPeriodEndDate) ?? ""
  } | ${formattedTaxType}${titleSuffix ? ` | ${titleSuffix}` : ""}`.trim();
};

const GaiaobjectName = (gaiaobjectNameProps: IGaiaobjectNameProps) => {
  const gaiaobjectName = getGaiaobjectName(gaiaobjectNameProps);
  const customType =
    gaiaobjectNameProps.category === "notice" ||
    gaiaobjectNameProps.category === "litigation"
      ? gaiaobjectNameProps.category
      : "tax";
  return (
    <FormField
      label="Name"
      description={`${cap(
        gaiaobjectNameProps.category!
      )} name is automatically generated based on the entity code, entity name (truncated to 40 characters), tax authority, tax type, ${customType} type, and ${
        gaiaobjectNameProps.category
      } period.`}
    >
      <Input value={gaiaobjectName} readOnly={true} />
    </FormField>
  );
};
export default GaiaobjectName;
